import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../AuthProvider';
import LoginPage from '../LoginPage';
import AlertModal from '../AlertModal';
import { PricingTier, BillingCycle } from '../../types';
import { createCheckoutSessionSafely } from '../../utils/subscriptionApi';
import { BrainCircuit, CreditCard, CheckCircle, Sparkles } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Helper function to validate plan and billing cycle from URL parameters
const validatePlanParams = (plan: string, billing: string): { plan: PricingTier; billing: BillingCycle } | null => {
  const validPlans = Object.values(PricingTier);
  const validBillingCycles = Object.values(BillingCycle);
  
  const normalizedPlan = plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
  const normalizedBilling = billing.charAt(0).toUpperCase() + billing.slice(1).toLowerCase();
  
  if (validPlans.includes(normalizedPlan as PricingTier) && 
      validBillingCycles.includes(normalizedBilling as BillingCycle)) {
    return {
      plan: normalizedPlan as PricingTier,
      billing: normalizedBilling as BillingCycle
    };
  }
  
  return null;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showSubscriptionMessage, setShowSubscriptionMessage] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });

  // Parse URL parameters for plan information
  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get('plan');
  const billingParam = searchParams.get('billing');
  const originParam = searchParams.get('origin');

  // Debug logging for URL parameters
  if (planParam || billingParam || originParam) {
    console.log('URL Parameters detected:', { planParam, billingParam, originParam });
  }

  // Validate plan parameters if they exist
  const planInfo = planParam && billingParam ? validatePlanParams(planParam, billingParam) : null;

  if (planInfo) {
    console.log('Validated plan info:', planInfo);
  }

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  // Cleanup effect to reset redirecting state when URL parameters change or component unmounts
  useEffect(() => {
    // Reset redirecting state when URL parameters change
    setIsRedirecting(false);
  }, [location.search]);

  // Safety timeout to prevent user from getting stuck in loading state
  useEffect(() => {
    if (isRedirecting) {
      const timeout = setTimeout(() => {
        console.warn('Checkout redirect timeout - clearing redirecting state');
        setIsRedirecting(false);
        navigate('/app', { replace: true });
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isRedirecting, navigate]);

  // Handle post-login redirect for pricing flow
  const handleLoginSuccess = async (user: any) => {
    if (originParam === 'pricing' && planInfo) {
      try {
        console.log('Redirecting to checkout after login for plan:', planInfo.plan);
        setIsRedirecting(true); // Set loading state before starting checkout
        await createCheckoutSessionSafely(planInfo.plan, planInfo.billing);
        // Note: We don't clear isRedirecting here because the page will redirect to Stripe
      } catch (error) {
        console.error('Error creating checkout session after login:', error);
        setIsRedirecting(false); // Clear loading state on error
        
        // Check if this is an "already have subscription" error
        if (error instanceof Error && error.message.includes('already have an active')) {
          // Show a friendly message and redirect to app
          setSubscriptionMessage('You already have an active subscription! Redirecting you to ThreadIt...');
          setShowSubscriptionMessage(true);
          setTimeout(() => {
            setShowSubscriptionMessage(false);
            navigate('/app', { replace: true });
          }, 5000); // Increased to 5 seconds for better readability
        } else {
          // Show generic error for other issues
          showAlert(
            'Checkout Error',
            'Unable to start checkout. Please try again from the pricing page.',
            'danger'
          );
          navigate('/app', { replace: true });
        }
      }
    } else {
      // Default behavior - redirect to canvas
      navigate('/app', { replace: true });
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500 rounded-full mb-4 shadow-lg">
            <BrainCircuit className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Loading ThreadIt
          </h1>
          <p className="text-slate-600 mb-6">
            Please wait while we set up your workspace...
          </p>
          
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="relative">
                <div className="w-8 h-8 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-8 h-8 border-2 border-transparent border-t-emerald-400 rounded-full animate-spin" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  Initializing...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show login page with plan info
  if (!user) {
    return (
      <LoginPage 
        isOpen={true} 
        onLoginSuccess={handleLoginSuccess}
      />
    );
  }

  // If user is authenticated but we're redirecting to checkout, show fancy loading state
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500 rounded-full mb-4 shadow-lg">
              <BrainCircuit className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Preparing Your Checkout
            </h1>
            <p className="text-slate-600">
              Setting up your subscription securely...
            </p>
          </div>

          {/* Plan Information Card */}
          {planInfo && (
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {planInfo.plan} Plan
                    </h3>
                    <p className="text-sm text-slate-500 capitalize">
                      {planInfo.billing} billing
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 text-emerald-600">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Premium</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Secure payment processing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Instant access to features</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Cancel anytime</span>
                </div>
              </div>
            </div>
          )}

          {/* Animated Loading */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="relative">
                <div className="w-8 h-8 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-8 h-8 border-2 border-transparent border-t-emerald-400 rounded-full animate-spin" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700 mb-1">
                  Connecting to Stripe
                </p>
                <p className="text-xs text-slate-500">
                  This will only take a moment...
                </p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-slate-500">
                Redirecting to secure checkout...
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-slate-400">
              Powered by Stripe • SSL Secured
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If user is authenticated and not redirecting, show the protected content
  return (
    <>
      {children}
      
      {/* Subscription Message Modal */}
      {showSubscriptionMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500 rounded-full mb-4 shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Welcome Back!
            </h3>
            <p className="text-slate-600 mb-6">
              {subscriptionMessage}
            </p>
            <div className="flex items-center justify-center space-x-2 text-emerald-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
